import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faCloud,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import React from "react";

/** Container styling for file-type icons in Mission Control / artifact cards. */
export type FileIconCardTone = {
  bg: string;
  text: string;
};

type FileTypeConfig = {
  icon: IconDefinition;
  color: string;
  cardTone: FileIconCardTone;
  label: string;
  mimeTypes: string[];
  extensions: string[];
};

// Constants — shared with Documents, Chat-style attachments, and artifact cards.
const FILE_TYPE_CONFIGS: Record<string, FileTypeConfig> = {
  pdf: {
    icon: faFilePdf,
    color: "text-red-400",
    cardTone: {
      bg: "bg-red-500/10",
      text: "text-red-700 dark:text-red-300",
    },
    label: "PDF",
    mimeTypes: ["application/pdf"],
    extensions: ["pdf"],
  },
  word: {
    icon: faFileWord,
    color: "text-blue-400",
    cardTone: {
      bg: "bg-blue-500/10",
      text: "text-blue-700 dark:text-blue-300",
    },
    label: "Word Document",
    mimeTypes: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    extensions: ["doc", "docx"],
  },
  excel: {
    icon: faFileExcel,
    color: "text-green-400",
    cardTone: {
      bg: "bg-green-500/10",
      text: "text-green-700 dark:text-green-300",
    },
    label: "Excel Spreadsheet",
    mimeTypes: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    extensions: ["xls", "xlsx"],
  },
  csv: {
    icon: faFileExcel,
    color: "text-green-400",
    cardTone: {
      bg: "bg-green-500/10",
      text: "text-green-700 dark:text-green-300",
    },
    label: "CSV",
    mimeTypes: ["text/csv"],
    extensions: ["csv"],
  },
  powerpoint: {
    icon: faFilePowerpoint,
    color: "text-slate-500",
    cardTone: {
      bg: "bg-orange-500/10",
      text: "text-orange-700 dark:text-orange-300",
    },
    label: "PowerPoint",
    mimeTypes: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    extensions: ["ppt", "pptx"],
  },
  image: {
    icon: faFileImage,
    color: "text-slate-400",
    cardTone: {
      bg: "bg-slate-500/10",
      text: "text-slate-600 dark:text-slate-300",
    },
    label: "Image",
    mimeTypes: ["image/"],
    extensions: ["jpg", "jpeg", "png", "gif", "svg"],
  },
  cloud: {
    icon: faCloud,
    color: "text-slate-400",
    cardTone: {
      bg: "bg-slate-500/10",
      text: "text-slate-600 dark:text-slate-300",
    },
    label: "Cloud Document",
    mimeTypes: ["cloud_document"],
    extensions: [],
  },
};

const DEFAULT_ICON_CONFIG: FileTypeConfig = {
  icon: faFileAlt,
  color: "text-slate-400",
  cardTone: {
    bg: "bg-slate-500/10",
    text: "text-slate-600 dark:text-slate-300",
  },
  label: "Unknown",
  mimeTypes: [],
  extensions: [],
};

export type FileIconVisual = {
  icon: IconDefinition;
  tone: FileIconCardTone;
};

/**
 * Resolve icon + card tone from MIME type and/or filename (extension).
 * Returns ``null`` when the type is not recognised (e.g. artifact cards).
 */
export function getFileIconVisual(
  mimeType?: string | null,
  filename?: string | null,
): FileIconVisual | null {
  const config = resolveFileConfig(mimeType, filename);
  if (!config || config === DEFAULT_ICON_CONFIG) return null;
  return { icon: config.icon, tone: config.cardTone };
}

/**
 * Renders an icon component for a given file type
 * @param fileType - MIME type or file extension
 * @returns React component with appropriate icon and styling
 */
export function getFileTypeIcon(fileType: string): React.ReactElement {
  const config = getFileConfig(fileType);
  return (
    <FontAwesomeIcon icon={config.icon} className={`${config.color} text-xl`} />
  );
}

/**
 * Returns a human-readable file type description
 * @param fileType - MIME type or file extension
 * @returns Formatted string describing the file type
 */
export function getReadableFileType(fileType: string): string {
  if (!fileType) return DEFAULT_ICON_CONFIG.label;

  const mimeType = fileType.toLowerCase();
  const config = getFileConfig(mimeType);

  // Handle image types specially to include the specific format
  if (config === FILE_TYPE_CONFIGS.image) {
    const imageType = mimeType.includes("/")
      ? mimeType.split("/")[1].toUpperCase()
      : mimeType.toUpperCase();
    return `${imageType} Image`;
  }

  // Handle extension-only file types
  if (!mimeType.includes("/")) {
    return mimeType.toUpperCase();
  }

  return config.label;
}

/**
 * Formats a file size in bytes to a human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted string with appropriate unit
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function extensionFromFilename(filename?: string | null): string | null {
  if (!filename?.trim()) return null;
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) return null;
  return filename.slice(dot + 1).toLowerCase();
}

function configForToken(token: string): FileTypeConfig | null {
  const normalizedType = token.toLowerCase();
  return (
    Object.values(FILE_TYPE_CONFIGS).find((config) => {
      return (
        config.mimeTypes.some((mime) => normalizedType.includes(mime)) ||
        config.extensions.some((ext) => normalizedType === ext)
      );
    }) ?? null
  );
}

/** @param fileType - MIME type or bare extension */
function getFileConfig(fileType: string): FileTypeConfig {
  if (!fileType) return DEFAULT_ICON_CONFIG;
  return configForToken(fileType) ?? DEFAULT_ICON_CONFIG;
}

function resolveFileConfig(
  mimeType?: string | null,
  filename?: string | null,
): FileTypeConfig | null {
  if (mimeType?.trim()) {
    const fromMime = configForToken(mimeType.trim());
    if (fromMime) return fromMime;
  }
  const ext = extensionFromFilename(filename);
  if (ext) {
    const fromExt = configForToken(ext);
    if (fromExt) return fromExt;
  }
  return null;
}
